/**
 * "Companies built & funded on the Lev process" strip on the redesigned homepage
 * (landing-home-v2): an endless horizontal scroll of company logos, each on
 * its brand-colored tile.
 */
import type { CSSProperties } from "react";
import Image from "next/image";
import { cn } from "@/lib/utils";

interface LevProcessCompany {
  name: string;
  /** Tailwind background classes for the tile. */
  tone: string;
  /**
   * Monochrome black logo from psl.com/companies, cropped to its content.
   * width/height are the file's intrinsic size (aspect ratio for next/image).
   */
  logo: { src: string; width: number; height: number };
  /** Renders the black logo white, for dark tiles. */
  invertLogo?: boolean;
  /** Low-res pixel-art logo: scale without smoothing. */
  pixelated?: boolean;
}

const LEV_PROCESS_COMPANIES: readonly LevProcessCompany[] = [
  {
    name: "Dropzone AI",
    tone: "bg-black",
    logo: { src: "/landing/companies/dropzone-ai.png", width: 408, height: 76 },
    invertLogo: true,
  },
  {
    name: "Recurrent",
    tone: "bg-[#2E3192]",
    logo: { src: "/landing/companies/recurrent.svg", width: 480, height: 58 },
    invertLogo: true,
  },
  {
    name: "TrovaTrip",
    tone: "bg-[#23201E]",
    logo: { src: "/landing/companies/trovatrip.png", width: 408, height: 100 },
    invertLogo: true,
  },
  {
    name: "Shipium",
    tone: "bg-[#E8735F]",
    logo: { src: "/landing/companies/shipium.svg", width: 350, height: 90 },
  },
  {
    name: "AG2",
    tone: "bg-gradient-to-br from-[#B4F2D8] to-[#7EE6C6]",
    logo: { src: "/landing/companies/ag2.png", width: 90, height: 50 },
    pixelated: true,
  },
  {
    name: "Archera",
    tone: "bg-[#130F3A]",
    logo: { src: "/landing/companies/archera.svg", width: 520, height: 106 },
    invertLogo: true,
  },
];

function CompanyTile({ company }: { company: LevProcessCompany }) {
  return (
    <li
      className={cn(
        "flex h-[52px] w-[160px] shrink-0 items-center justify-center rounded-lg px-4",
        company.tone,
      )}
    >
      <Image
        src={company.logo.src}
        alt={company.name}
        width={company.logo.width}
        height={company.logo.height}
        unoptimized={company.pixelated}
        className={cn(
          "h-auto max-h-6 w-auto max-w-full object-contain",
          company.invertLogo && "brightness-0 invert",
          company.pixelated && "[image-rendering:pixelated]",
        )}
      />
    </li>
  );
}

export function LandingLogoStrip() {
  return (
    <section className="bg-landing-bg-light flex flex-col items-center gap-6 pb-4 md:pb-6">
      <div className="bg-landing-bg-medium px-2.5 py-2 rounded-md">
        <h2 className="font-mono text-sm text-landing-secondary uppercase">
          Companies built &amp; funded on the Lev process
        </h2>
      </div>

      <div className="relative w-full max-w-[1024px] overflow-hidden">
        <div className="absolute left-0 top-0 bottom-0 w-16 md:w-24 bg-gradient-to-r from-landing-bg-light to-transparent z-card-above pointer-events-none" />
        <div className="absolute right-0 top-0 bottom-0 w-16 md:w-24 bg-gradient-to-l from-landing-bg-light to-transparent z-card-above pointer-events-none" />

        {/* animate-scroll-left translates by -50%: the list is rendered twice, each copy
            carrying its own trailing gap (pr-4), so -50% lands exactly on the second copy. */}
        <div
          className="flex w-max animate-scroll-left hover:[animation-play-state:paused] motion-reduce:animate-none"
          style={{ "--scroll-duration": "30s" } as CSSProperties}
        >
          <ul className="flex gap-4 pr-4">
            {LEV_PROCESS_COMPANIES.map((company) => (
              <CompanyTile key={company.name} company={company} />
            ))}
          </ul>
          <ul className="flex gap-4 pr-4" aria-hidden="true">
            {LEV_PROCESS_COMPANIES.map((company) => (
              <CompanyTile key={company.name} company={company} />
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}
